import { createColumnHelper } from '@tanstack/react-table';
import { ResourceCategory } from '@mews-commander/data-access';
import { LocalizedEnum, lt } from '@mews/framework';
import { useResolveRoute } from '@mews/routing';
import { RouteName, ActionType } from '@mews-commander/enums';
import { Card, Container, IconName, Table, TableProvider, useTableState } from '@optimus-web/core';
import { WithGuideLink } from '@mews-commander/types';
import { RouteLink } from '@mews-commander/ui';
import { getEnterpriseName } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { orderOrderableEntityArray } from '@mews-commander/legacy-entities/src/utils/orderableEntities';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { BaseLayout } from '../../components/layout';
import { HeadingAction } from '../../legacy/React/Components/HeadingAction/Action/types';
import { Breadcrumbs } from '../../components/BreadcrumbList';
import { navigateInModal } from '../app/actions/common';
import { useServicesGetAllSuspenseQuery } from '../services';
import { ServiceName } from '../../components/commander-ui/ServiceName';
import { useResourceCategoryQuery } from './query';

const columnHelper = createColumnHelper<ResourceCategory>();
const getTableColumns = (openDetail: (resourceCategoryId: string) => void) => [
    columnHelper.accessor('name', {
        header: () => lt('Name'),
        cell: (props) => (
            <RouteLink
                route={RouteName.ResourceCategoryDetail}
                params={{ id: props.row.original.id }}
                onClick={() => openDetail(props.row.original.id)}
                wordBreak="break-all"
                inherit
            >
                {props.getValue()}
            </RouteLink>
        ),
    }),
    columnHelper.accessor('type', {
        header: () => lt('ResourceCategoryType'),
        cell: (props) => <LocalizedEnum prefix="ResourceCategoryType" value={props.getValue()} />,
    }),
    columnHelper.accessor('capacity', {
        header: () => lt('BedCount'),
    }),
    columnHelper.accessor('extraCapacity', {
        header: () => lt('ExtraCapacity'),
    }),
    columnHelper.accessor('ordering', {
        header: () => lt('Ordering'),
    }),
];

export interface ResourceCategoryListPageProps extends WithGuideLink {
    serviceId: string;
}

export const ResourceCategoryListPage: React.FC<ResourceCategoryListPageProps> = ({ guideLink, serviceId }) => {
    const dispatch = useCommanderThunkDispatch();
    const resolveRoute = useResolveRoute();

    const serviceQuery = useServicesGetAllSuspenseQuery(
        { serviceIds: [serviceId], enterpriseIds: [] },
        { select: ({ services }) => services[0] }
    );

    const service = serviceQuery.data;
    const enterpriseId = serviceQuery.data?.enterpriseId;

    const enterpriseName = useCommanderSelector((s) => getEnterpriseName(s, { enterpriseId }));

    const resourceCategoriesQuery = useResourceCategoryQuery(
        { serviceIds: [serviceId], enterpriseId },
        {
            select: ({ resourceCategories }) => ({
                resourceCategories: resourceCategories ? orderOrderableEntityArray(resourceCategories) : undefined,
            }),
            refetchOnMount: 'always',
            enabled: !!enterpriseId,
        }
    );

    const actions: HeadingAction[] = [
        {
            url: resolveRoute(RouteName.ResourceCategoryCreate, { query: { OwnerId: serviceId } }),
            icon: IconName.Add,
            text: lt('ResourceCategory'),
            type: ActionType.Link,
            actionId: 'add',
        },
    ];

    const openDetail = (id: string) => {
        const url = resolveRoute(RouteName.ResourceCategoryDetail, { id });
        dispatch(navigateInModal(url));
    };

    const columns = getTableColumns(openDetail);
    const [tableState, setTableState] = useTableState<ResourceCategory>({
        pagination: { show: false },
    });

    const breadcrumbs: Breadcrumbs = [
        { text: enterpriseName, url: resolveRoute(RouteName.EnterpriseDetail, { enterpriseId }) },
        { text: lt('ServicePlural'), url: resolveRoute(RouteName.ServiceIndex, { query: { OwnerId: enterpriseId } }) },
        { text: service && <ServiceName service={service} />, url: resolveRoute(RouteName.VisitDetail, { serviceId }) },
        { text: lt('ResourceCategoryPlural'), active: true },
    ];

    const data = resourceCategoriesQuery.data?.resourceCategories ?? [];

    return (
        <BaseLayout guideLink={guideLink} actions={actions} padded breadcrumbs={breadcrumbs}>
            <Card>
                <Container padding="200">
                    <TableProvider state={tableState} columns={columns} onStateChange={setTableState}>
                        <Table
                            uniqueRowId="id"
                            fixedFirstColumn
                            fixedHeader
                            data={data}
                            amountOfRows={data.length}
                            loading={resourceCategoriesQuery.isFetching}
                        />
                    </TableProvider>
                </Container>
            </Card>
        </BaseLayout>
    );
};
